import { Check, Clock3, FileText, Loader2, Lock } from "lucide-react";

function formatTimelineDate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function SaveState({ saving, savedAt }) {
  if (saving) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500">
        <Loader2 className="h-3 w-3 animate-spin" /> Saving…
      </span>
    );
  }
  if (savedAt) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
        <Check className="h-3 w-3" /> Saved
      </span>
    );
  }
  return null;
}

export default function VisitNoteCard({
  note,
  onChange,
  saving = false,
  savedAt = null,
  history = [],
}) {
  const rows = Array.isArray(history) ? history : [];

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-sm" data-testid="doctor-visit-note-card">
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-teal-600" />
          <h2 className="text-sm font-semibold text-slate-900">Visit note</h2>
        </div>
        <SaveState saving={saving} savedAt={savedAt} />
      </header>

      <div className="p-4">
        <textarea
          value={note}
          onChange={(event) => onChange(event.target.value)}
          placeholder="What happened today? Clinical findings, advice, or instructions…"
          rows={5}
          data-testid="doctor-visit-note-textarea"
          className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm leading-6 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
        />
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500">
          <Lock className="h-3 w-3 text-slate-400" />
          <span>Saved automatically. Checkout locks this note permanently.</span>
        </div>
      </div>

      {rows.length > 0 ? (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-4" data-testid="doctor-visit-note-history">
          <div className="mb-3 flex items-center gap-2">
            <Clock3 className="h-4 w-4 text-slate-500" />
            <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-600">
              Previous visit notes
            </h3>
            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200">
              {rows.length}
            </span>
          </div>

          <ol className="space-y-0">
            {rows.map((entry, index) => (
              <li
                key={entry.id}
                className="relative pl-6 pb-4 last:pb-0"
                data-testid={`doctor-visit-note-history-${entry.id}`}
              >
                {index < rows.length - 1 ? (
                  <span className="absolute left-[5px] top-3 h-full w-px bg-slate-200" aria-hidden="true" />
                ) : null}
                <span className="absolute left-0 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-teal-500 ring-1 ring-teal-200" aria-hidden="true" />
                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <time className="text-[11px] font-semibold text-slate-600" dateTime={entry.recorded_at}>
                      {formatTimelineDate(entry.recorded_at)}
                    </time>
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-400">
                      <Lock className="h-2.5 w-2.5" /> Locked
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-5 text-slate-800">{entry.note_text}</p>
                  <p className="mt-1 text-[11px] text-slate-400">{entry.doctor_name || "Doctor"}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </section>
  );
}
